import { useEffect } from 'react'

import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverAction,
  PopoverContent,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from '@/components/ui/popover'
import { PencilIcon } from 'lucide-react'

import { NodeViewWrapper } from '@tiptap/react'

import { VariableNodeAttributes } from '~shared/richtext/nodes/variable.node'
import { RundownPermission } from '~shared/types'

import { useVariable } from '@/entities/variables/api/hooks'

import { useRundownPermission } from '@/features/permissions/lib/use-rundown-permission'
import type {
  NodeComponentProps,
  NodeViewLiveProps,
  NodeViewStaticProps,
} from '@/features/richtext/tiptap.helpers'
import { modalActions } from '@/features/rundown/modals/modal.store'
import { rundownPageStore } from '@/features/rundown/rundown-page.store'

import { variablesExtensionConfig } from './variables.config'

export function VariableNodeViewLive({
  node,
  updateAttributes,
  HTMLAttributes,
  ref,
}: NodeViewLiveProps<HTMLSpanElement>) {
  const attrs = node.attrs as VariableNodeAttributes

  const { data: variable } = useVariable({
    params: {
      rundownId: rundownPageStore.rundown.id,
      variableKey: attrs.variableKey,
    },
  })

  useEffect(() => {
    if (variable) {
      updateAttributes({
        fallbackValue: variable.value,
      })
    }
  }, [variable, updateAttributes])

  return (
    <NodeViewWrapper
      as="span"
      // Spread node data-* attrs
      {...HTMLAttributes}
    >
      <VariableNodeComponent
        node={node}
        ref={ref}
      />
    </NodeViewWrapper>
  )
}

export function VariableNodeViewStatic({ node }: NodeViewStaticProps) {
  return <VariableNodeComponent node={node} />
}

function VariableNodeComponent({
  node,
  ...props
}: NodeComponentProps & React.ComponentProps<'span'>) {
  const attrs = node.attrs as VariableNodeAttributes

  const char = variablesExtensionConfig.character

  const { data: variable } = useVariable({
    params: {
      rundownId: rundownPageStore.rundown.id,
      variableKey: attrs.variableKey,
    },
  })

  const hasWriteAccess = useRundownPermission({
    permission: RundownPermission.EDIT,
  })

  if (!variable) {
    return (
      <span
        className="inline-block hover:underline cursor-wait"
        {...props}
      >
        {char}
        {attrs.fallbackValue}
      </span>
    )
  }

  return (
    <Popover>
      <PopoverTrigger
        nativeButton={false}
        tabIndex={-1}
        render={
          <span
            className="inline-block cursor-help hover:underline"
            {...props}
          >
            <span className="opacity-50 text-[11px]">{char}</span>
            {variable?.value ?? attrs.fallbackValue}
          </span>
        }
      />
      <PopoverContent initialFocus={false}>
        <PopoverHeader>
          <PopoverTitle>Text Variable</PopoverTitle>

          <PopoverAction>
            <Button
              aria-label="Edit variable"
              variant="ghost"
              size="icon-sm"
              disabled={!hasWriteAccess}
              onClick={() => modalActions.open('VARIABLES', {})}
            >
              <PencilIcon />
            </Button>
          </PopoverAction>
        </PopoverHeader>

        <dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1">
          <dt className="text-muted-foreground">Name:</dt>
          <dd className="font-mono text-foreground break-all">
            {char}
            {attrs.variableKey}
          </dd>

          <dt className="text-muted-foreground">Value:</dt>
          <dd className="font-mono text-foreground break-all">
            {variable?.value}
          </dd>
        </dl>
      </PopoverContent>
    </Popover>
  )
}
